/**
 * CURRENT / PREVIOUS / FALLBACK slots — activate only after integrity passes.
 */
export class PlaylistCache {
    current = null;
    previous = null;
    fallback = null;
    staged = new Map();
    key(ref) {
        return `${ref.playlistId}@${ref.version}`;
    }
    getCurrent() {
        return this.current;
    }
    get(ref) {
        const k = this.key(ref);
        if (this.current && this.key(this.current.ref) === k)
            return this.current;
        if (this.previous && this.key(this.previous.ref) === k)
            return this.previous;
        if (this.fallback && this.key(this.fallback.ref) === k)
            return this.fallback;
        return this.staged.get(k) ?? null;
    }
    stage(playlist) {
        this.staged.set(this.key(playlist.ref), playlist);
    }
    /**
     * Promotes a staged/verified playlist to CURRENT.
     * Previous CURRENT becomes PREVIOUS; first successful activate seeds FALLBACK.
     * Returns null when the requested ref is already CURRENT (no playback restart).
     */
    activate(ref) {
        const next = this.get(ref);
        if (!next)
            return null;
        if (this.current && this.key(this.current.ref) === this.key(ref)) {
            this.staged.delete(this.key(ref));
            return null;
        }
        if (this.current) {
            this.previous = this.current;
        }
        this.current = next;
        if (!this.fallback)
            this.fallback = next;
        this.staged.delete(this.key(ref));
        return next;
    }
    /** Roll back to PREVIOUS, else FALLBACK — never blank if a verified set exists. */
    rollback() {
        if (this.previous) {
            this.current = this.previous;
            return this.current;
        }
        if (this.fallback) {
            this.current = this.fallback;
            return this.current;
        }
        return this.current;
    }
    clear() {
        this.current = null;
        this.previous = null;
        this.fallback = null;
        this.staged.clear();
    }
    /** Durable snapshot for IndexedDB / cold-start restore. */
    toSnapshot() {
        return {
            current: this.current,
            previous: this.previous,
            fallback: this.fallback,
            staged: [...this.staged.values()],
        };
    }
    /** Replace in-memory slots from a previously verified durable snapshot. */
    hydrate(snapshot) {
        this.clear();
        this.current = snapshot.current;
        this.previous = snapshot.previous;
        this.fallback = snapshot.fallback;
        for (const playlist of snapshot.staged) {
            this.staged.set(this.key(playlist.ref), playlist);
        }
    }
}
//# sourceMappingURL=playlist-cache.js.map